import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { useAuth } from "@/projects/lib/useStore";
import {
  FileVideo,
  Clock,
  Trophy,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { StatusBadge } from "@/projects/components/status-badge";
import { cn } from "@/projects/lib/utils";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  // eslint-disable-next-line import/no-deprecated -- Cell still required for pie slice colors
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import {
  fetchTeacherDashboard,
  formatDashboardDate,
  getDashboardApiErrorMessage,
  normalizeDashboardStatus,
  type TeacherDashboardData,
  type TeacherSummaryCard,
} from "@/projects/lib/teacherDashboardApi";
import { toast } from "@/projects/lib/toast";

const C = {
  royal: "oklch(0.42 0.15 265)",
  gold: "oklch(0.78 0.14 85)",
  saffron: "oklch(0.72 0.18 55)",
  success: "oklch(0.62 0.16 145)",
  danger: "oklch(0.58 0.22 27)",
};

const CARD_META: Record<
  string,
  {
    icon: typeof FileVideo;
    tone: "royal" | "saffron" | "success" | "danger";
  }
> = {
  total_assigned: { icon: FileVideo, tone: "royal" },
  pending_verification: { icon: Clock, tone: "saffron" },
  ready_for_reward: { icon: Trophy, tone: "success" },
  not_proper: { icon: AlertTriangle, tone: "danger" },
};

const OUTCOME_COLORS: Record<string, string> = {
  ready_for_reward: C.success,
  not_proper: C.danger,
  pending: C.saffron,
  assigned_for_verification: C.saffron,
};

function cardValue(
  cards: TeacherSummaryCard[],
  key: string,
  fallback = 0
): number {
  return cards.find(c => c.key === key)?.value ?? fallback;
}

export function TeacherDashboard() {
  const auth = useAuth();
  const [data, setData] = useState<TeacherDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const dashboard = await fetchTeacherDashboard();
        if (!cancelled) {
          setData(dashboard);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            getDashboardApiErrorMessage(
              error,
              "Failed to load teacher dashboard."
            )
          );
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pending = data
    ? cardValue(data.summary_cards, "pending_verification")
    : 0;

  const kpis = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.summary_cards.map(card => {
      const meta = CARD_META[card.key] ?? {
        icon: FileVideo,
        tone: "royal" as const,
      };
      return {
        key: card.key,
        label: card.label,
        value: card.value,
        delta: card.trend_percent,
        up: card.trend_direction !== "down",
        icon: meta.icon,
        tone: meta.tone,
      };
    });
  }, [data]);

  const activitySeries = useMemo(
    () =>
      (data?.verification_activity.series ?? []).map(point => ({
        date: formatDashboardDate(point.date),
        assigned: point.assigned,
        verified: point.verified,
      })),
    [data]
  );

  const throughputSeries = useMemo(
    () =>
      (data?.daily_throughput.series ?? []).map(point => ({
        date: formatDashboardDate(point.date),
        verified: point.verified,
      })),
    [data]
  );

  const outcome = useMemo(
    () =>
      (data?.outcome_split.items ?? []).map(item => ({
        name: item.label,
        value: item.count,
        color: OUTCOME_COLORS[item.status] ?? C.royal,
      })),
    [data]
  );

  const completion = data?.completion.percent ?? 0;
  const gauge = [{ name: "rate", value: completion, fill: C.gold }];
  const recent = data?.recent_assignments.items ?? [];

  return (
    <DashboardLayout
      role="teacher"
      title={`Welcome, ${auth?.name ?? "Teacher"}`}
      breadcrumb={["Home", "Dashboard"]}
    >
      {loading ? (
        <div className="text-muted-foreground flex items-center justify-center gap-2 py-24 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading dashboard…
        </div>
      ) : !data ? (
        <div className="text-muted-foreground py-24 text-center text-sm">
          Unable to load dashboard data.
        </div>
      ) : (
        <>
          <div className="from-royal via-royal to-royal/80 shadow-card mb-4 overflow-hidden rounded-xl border bg-gradient-to-r p-5 text-white">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-gold text-xs font-semibold tracking-widest uppercase">
                  Verification Console
                </div>
                <div className="mt-1 text-xl font-bold sm:text-2xl">
                  Good to see you, {auth?.name?.split(" ")[0] ?? "Teacher"} 👋
                </div>
                <div className="mt-1 text-sm text-white/80">
                  You have{" "}
                  <span className="text-gold font-semibold">{pending}</span>{" "}
                  videos awaiting review.
                </div>
              </div>
              <Link
                to="/teacher/submissions"
                className="bg-gold text-primary hover:bg-gold/90 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold"
              >
                Review Queue <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map(k => (
              <div
                key={k.key}
                className="bg-card shadow-soft relative overflow-hidden rounded-xl border p-4"
              >
                <div
                  className={cn(
                    "absolute inset-x-0 top-0 h-1",
                    k.tone === "royal" && "bg-royal",
                    k.tone === "saffron" && "bg-saffron",
                    k.tone === "success" && "bg-success",
                    k.tone === "danger" && "bg-destructive"
                  )}
                />
                <div className="flex items-start justify-between">
                  <div
                    className={cn(
                      "grid h-9 w-9 place-items-center rounded-lg",
                      k.tone === "royal" && "bg-royal-soft text-royal",
                      k.tone === "saffron" && "bg-saffron-soft text-saffron",
                      k.tone === "success" && "bg-success/10 text-success",
                      k.tone === "danger" &&
                        "bg-destructive/10 text-destructive"
                    )}
                  >
                    <k.icon className="h-4.5 w-4.5" />
                  </div>
                  <div
                    className={cn(
                      "flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                      k.up
                        ? "bg-success/10 text-success"
                        : "bg-destructive/10 text-destructive"
                    )}
                  >
                    {k.up ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {k.delta}%
                  </div>
                </div>
                <div className="mt-3 text-2xl font-bold">{k.value}</div>
                <div className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                  {k.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-6">
            <Panel
              title={data.verification_activity.title}
              subtitle={data.verification_activity.subtitle}
              className="lg:col-span-4"
            >
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart
                  data={activitySeries}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="t-a" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.royal} stopOpacity={0.5} />
                      <stop
                        offset="100%"
                        stopColor={C.royal}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                    <linearGradient id="t-v" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={C.success}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="100%"
                        stopColor={C.success}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis
                    dataKey="date"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Area
                    type="monotone"
                    dataKey="assigned"
                    name="Assigned"
                    stroke={C.royal}
                    strokeWidth={2}
                    fill="url(#t-a)"
                  />
                  <Area
                    type="monotone"
                    dataKey="verified"
                    name="Verified"
                    stroke={C.success}
                    strokeWidth={2}
                    fill="url(#t-v)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <Panel
              title={data.completion.title}
              subtitle={data.completion.subtitle}
              className="lg:col-span-2"
            >
              <div className="relative">
                <ResponsiveContainer width="100%" height={220}>
                  <RadialBarChart
                    innerRadius="70%"
                    outerRadius="100%"
                    data={gauge}
                    startAngle={220}
                    endAngle={-40}
                  >
                    <PolarAngleAxis
                      type="number"
                      domain={[0, 100]}
                      tick={false}
                    />
                    <RadialBar
                      dataKey="value"
                      cornerRadius={12}
                      background={{ fill: "oklch(0.94 0.02 265)" }}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-royal text-4xl font-extrabold">
                    {data.completion.percent}%
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {data.completion.label}
                  </div>
                </div>
              </div>
              <div className="bg-muted/40 mt-2 flex items-center justify-between rounded-lg px-3 py-2 text-xs">
                <span className="text-muted-foreground">Approval rate</span>
                <span className="text-success font-semibold">
                  {data.completion.approval_rate_percent}%
                </span>
              </div>
            </Panel>

            <Panel
              title={data.outcome_split.title}
              subtitle={data.outcome_split.subtitle}
              className="lg:col-span-2"
            >
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={outcome}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {outcome.map(s => (
                      <Cell key={s.name} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 text-xs">
                {outcome.map(o => (
                  <div key={o.name} className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: o.color }}
                    />
                    <span className="text-muted-foreground">{o.name}</span>
                    <span className="ml-auto font-semibold">{o.value}</span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel
              title={data.daily_throughput.title}
              subtitle={data.daily_throughput.subtitle}
              className="lg:col-span-4"
            >
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={throughputSeries}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis
                    dataKey="date"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar
                    dataKey="verified"
                    name="Verified"
                    fill={C.gold}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <Panel
            title={data.recent_assignments.title}
            subtitle={data.recent_assignments.subtitle}
            className="mt-4"
          >
            {recent.length === 0 ? (
              <div className="text-muted-foreground py-10 text-center text-sm">
                No submissions are currently assigned to you.
              </div>
            ) : (
              <div className="divide-y">
                {recent.map(s => (
                  <Link
                    key={String(s.id)}
                    to="/teacher/verify/$id"
                    params={{ id: String(s.id) }}
                    className="hover:bg-muted/40 flex items-center gap-3 py-2.5"
                  >
                    <div className="bg-royal-soft text-royal grid h-9 w-9 place-items-center rounded-lg">
                      <FileVideo className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {s.title}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {s.student_name} · {s.student_id} ·{" "}
                        <span className="font-mono">{s.reference}</span>
                      </div>
                    </div>
                    <StatusBadge
                      status={normalizeDashboardStatus(
                        s.status_display || s.status
                      )}
                    />
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-3 flex justify-end">
              <Link
                to="/teacher/submissions"
                className="text-royal inline-flex items-center gap-1 text-xs font-medium hover:underline"
              >
                View all assignments <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </Panel>
        </>
      )}
    </DashboardLayout>
  );
}

function Panel({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "bg-card shadow-soft rounded-xl border p-4 sm:p-5",
        className
      )}
    >
      <div className="mb-3">
        <div className="text-sm font-semibold">{title}</div>
        {subtitle && (
          <div className="text-muted-foreground text-[11px]">{subtitle}</div>
        )}
      </div>
      {children}
    </div>
  );
}
